import {
  Badge,
  Box,
  Button,
  Collapse,
  Divider,
  Flex,
  Icon,
  Image,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  UnorderedList,
} from '@chakra-ui/react'
import { Clock } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useAppVersions } from '../../../hooks/useSystem'
import { hasUnreadVersions, markVersionAsViewed, needsVersionRefresh, markVersionRefreshed } from '../../../utils/functions/versionTracking'
import { CheckCircle, Rocket } from '../../../utils/constants/icons'

interface VersionData {
  version: string
  date: string
  new_features?: string[]
  bug_fixes?: string[]
  planned_features?: string[]
}

interface NewVersionModalProps {
  isOpen?: boolean
  onOpen?: () => void
  onClose?: () => void
  autoShow?: boolean
}

const NewVersionModal = ({
  isOpen: controlledIsOpen,
  onOpen: controlledOnOpen,
  onClose: controlledOnClose,
  autoShow = true,
}: NewVersionModalProps = {}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false)
  const [hasChecked, setHasChecked] = useState(false)
  const [showPreviousVersions, setShowPreviousVersions] = useState(false)

  const { data: versionsRaw, isLoading } = useAppVersions()
  const versions: VersionData[] | undefined = Array.isArray(versionsRaw) ? versionsRaw : undefined

  // Use controlled state if provided, otherwise use internal state
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen

  useEffect(() => {
    if (autoShow && !isLoading && versions && versions.length > 0 && !hasChecked) {
      setHasChecked(true)

      const latestVersion = versions[0].version

      // If this version hasn't been refreshed for yet, refresh to load new chunks
      if (needsVersionRefresh(latestVersion)) {
        markVersionRefreshed(latestVersion)
        window.location.reload()
        return
      }

      if (hasUnreadVersions(versions)) {
        setTimeout(() => {
          if (controlledOnOpen) {
            controlledOnOpen()
          } else {
            setInternalIsOpen(true)
          }
        }, 1000)
      }
    }
  }, [versions, isLoading, hasChecked, autoShow, controlledOnOpen])

  const handleClose = () => {
    if (versions && versions.length > 0) {
      markVersionAsViewed(versions[0].version)
    }
    if (controlledOnClose) {
      controlledOnClose()
    } else {
      setInternalIsOpen(false)
    }
  }

  const latestVersion: VersionData | undefined = versions?.[0]
  const previousVersions: VersionData[] = versions?.slice(1) ?? []

  if (!latestVersion) return null

  const renderVersionSection = (version: VersionData, isCompact = false) => (
    <Box mb={isCompact ? '12px' : '16px'}>
      <Flex alignItems="center" gap="8px" mb="6px">
        <Badge
          bg="primary.500"
          color="white"
          textStyle="b-xs"
          p="4px 6px"
          borderRadius="6px"
          fontWeight="500"
          textTransform="none"
        >
          v{version.version}
        </Badge>
        <Text textStyle="b-xs" color="neutral.500" fontWeight="500">
          {new Date(version.date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </Text>
      </Flex>

      {version.new_features && version.new_features.length > 0 && (
        <Box mb={isCompact ? '8px' : '12px'}>
          <Flex alignItems="center" gap="6px" mb="6px">
            <Box
              w="20px"
              h="20px"
              borderRadius="6px"
              bg="primary.50"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon viewBox="0 0 24 24" w="12px" h="12px" color="primary.500" fill="none">
                <Rocket />
              </Icon>
            </Box>
            <Text textStyle="b-sm" color="neutral.900" fontWeight="600">
              New Features
            </Text>
          </Flex>
          <UnorderedList spacing="4px" pl="6px" ml="20px" styleType="disc" sx={{ 'li::marker': { color: 'black' } }}>
            {version.new_features.map((feature: string, idx: number) => (
              <ListItem key={idx} textStyle="b-xs" color="neutral.700" lineHeight="1.4">
                {feature}
              </ListItem>
            ))}
          </UnorderedList>
        </Box>
      )}

      {version.bug_fixes && version.bug_fixes.length > 0 && (
        <Box mb={isCompact ? '8px' : '12px'}>
          <Flex alignItems="center" gap="6px" mb="6px">
            <Box
              w="20px"
              h="20px"
              borderRadius="6px"
              bg="primary.50"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon viewBox="0 0 24 24" w="12px" h="12px" color="primary.500" fill="none">
                <CheckCircle />
              </Icon>
            </Box>
            <Text textStyle="b-sm" color="neutral.900" fontWeight="600">
              Bug Fixes
            </Text>
          </Flex>
          <UnorderedList spacing="4px" pl="6px" ml="20px" styleType="disc" sx={{ 'li::marker': { color: 'black' } }}>
            {version.bug_fixes.map((fix: string, idx: number) => (
              <ListItem key={idx} textStyle="b-xs" color="neutral.700" lineHeight="1.4">
                {fix}
              </ListItem>
            ))}
          </UnorderedList>
        </Box>
      )}

      {version.planned_features && version.planned_features.length > 0 && (
        <Box>
          <Flex alignItems="center" gap="6px" mb="6px">
            <Box
              w="20px"
              h="20px"
              borderRadius="6px"
              bg="primary.50"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon viewBox="0 0 24 24" w="12px" h="12px" color="primary.500" fill="none">
                <Clock />
              </Icon>
            </Box>
            <Text textStyle="b-sm" color="neutral.600" fontWeight="600" fontStyle="italic">
              Coming Soon
            </Text>
          </Flex>
          <UnorderedList spacing="4px" pl="6px" ml="20px" styleType="disc" sx={{ 'li::marker': { color: 'black' } }}>
            {version.planned_features.map((planned: string, idx: number) => (
              <ListItem key={idx} textStyle="b-xs" color="neutral.600" fontStyle="italic" lineHeight="1.4">
                {planned}
              </ListItem>
            ))}
          </UnorderedList>
        </Box>
      )}
    </Box>
  )

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size="lg">
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent borderRadius="16px" maxW="640px" boxShadow="0 8px 32px rgba(0, 0, 0, 0.12)">
        <ModalHeader pb="8px" pt="16px" px="20px">
          <Flex alignItems="center" gap="10px">
            <Box
              w="40px"
              h="40px"
              bgPos="center"
              bgSize="cover"
              bgRepeat="no-repeat"
              bgImage="/logo/react-rounded.svg"
              borderRadius="8px"
              flexShrink={0}
            />
            <Box flex="1">
              <Flex alignItems="center" gap="8px" mb="2px">
                <Text textStyle="h5" color="neutral.900" fontWeight="600">
                  What's New in{' '}
                  <Image src="/logo/blue-md.svg" alt="Ocean" h="32px" display="inline" verticalAlign="bottom" />
                </Text>
                <Badge
                  textTransform="none"
                  bg="primary.500"
                  color="white"
                  textStyle="b-xs"
                  p="4px 6px"
                  borderRadius="6px"
                  fontWeight="500"
                >
                  v{latestVersion.version}
                </Badge>
              </Flex>
              <Text textStyle="b-sm" color="neutral.600" fontWeight="500">
                {new Date(latestVersion.date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </Text>
            </Box>
          </Flex>
        </ModalHeader>

        <ModalCloseButton top="14px" right="14px" />

        <ModalBody pt="12px" pb="16px" px="20px" overflowY="auto" maxH="60vh">
          {latestVersion.new_features && latestVersion.new_features.length > 0 && (
            <Box mb="16px">
              <Flex alignItems="center" gap="8px" mb="8px">
                <Box
                  w="24px"
                  h="24px"
                  borderRadius="6px"
                  bg="primary.50"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  transition="all 0.3s ease"
                  cursor="pointer"
                  _hover={{
                    transform: 'scale(1.1) rotate(5deg)',
                    boxShadow: '0 4px 12px rgba(14, 4, 157, 0.3)',
                  }}
                >
                  <Icon viewBox="0 0 24 24" w="14px" h="14px" color="primary.500" fill="none">
                    <Rocket />
                  </Icon>
                </Box>
                <Text textStyle="b-md" color="neutral.900" fontWeight="600">
                  New Features
                </Text>
              </Flex>
              <UnorderedList
                spacing="6px"
                pl="8px"
                ml="24px"
                styleType="disc"
                sx={{ 'li::marker': { color: 'black' } }}
              >
                {latestVersion.new_features.map((feature: string, idx: number) => (
                  <ListItem key={idx} textStyle="b-sm" color="neutral.700" lineHeight="1.5">
                    {feature}
                  </ListItem>
                ))}
              </UnorderedList>
            </Box>
          )}

          {latestVersion.bug_fixes && latestVersion.bug_fixes.length > 0 && (
            <Box mb="16px">
              <Flex alignItems="center" gap="8px" mb="8px">
                <Box
                  w="24px"
                  h="24px"
                  borderRadius="6px"
                  bg="primary.50"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  transition="all 0.3s ease"
                  cursor="pointer"
                  _hover={{
                    transform: 'scale(1.1) rotate(-5deg)',
                    boxShadow: '0 4px 12px rgba(14, 4, 157, 0.3)',
                  }}
                >
                  <Icon viewBox="0 0 24 24" w="14px" h="14px" color="primary.500" fill="none">
                    <CheckCircle />
                  </Icon>
                </Box>
                <Text textStyle="b-md" color="neutral.900" fontWeight="600">
                  Bug Fixes
                </Text>
              </Flex>
              <UnorderedList
                spacing="6px"
                pl="8px"
                ml="24px"
                styleType="disc"
                sx={{ 'li::marker': { color: 'black' } }}
              >
                {latestVersion.bug_fixes.map((fix: string, idx: number) => (
                  <ListItem key={idx} textStyle="b-sm" color="neutral.700" lineHeight="1.5">
                    {fix}
                  </ListItem>
                ))}
              </UnorderedList>
            </Box>
          )}

          {latestVersion.planned_features && latestVersion.planned_features.length > 0 && (
            <Box>
              <Flex alignItems="center" gap="8px" mb="8px">
                <Box
                  w="24px"
                  h="24px"
                  borderRadius="6px"
                  bg="primary.50"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  transition="all 0.3s ease"
                  cursor="pointer"
                  _hover={{
                    transform: 'scale(1.1) rotate(5deg)',
                    boxShadow: '0 4px 12px rgba(14, 4, 157, 0.3)',
                  }}
                >
                  <Icon viewBox="0 0 24 24" w="14px" h="14px" color="primary.500" fill="none">
                    <Clock />
                  </Icon>
                </Box>
                <Text textStyle="b-md" color="neutral.600" fontWeight="600" fontStyle="italic">
                  Coming Soon
                </Text>
              </Flex>
              <UnorderedList
                spacing="6px"
                pl="8px"
                ml="24px"
                styleType="disc"
                sx={{ 'li::marker': { color: 'black' } }}
              >
                {latestVersion.planned_features.map((planned: string, idx: number) => (
                  <ListItem key={idx} textStyle="b-sm" color="neutral.600" fontStyle="italic" lineHeight="1.5">
                    {planned}
                  </ListItem>
                ))}
              </UnorderedList>
            </Box>
          )}

          {previousVersions.length > 0 && (
            <Collapse in={showPreviousVersions} animateOpacity>
              <Divider my="16px" borderColor="neutral.200" />
              <Box>
                <Text textStyle="b-md" color="neutral.900" fontWeight="600" mb="12px">
                  Previous Versions
                </Text>
                {previousVersions.map((version, idx) => (
                  <Box key={idx} mb={idx < previousVersions.length - 1 ? '16px' : '0'}>
                    {renderVersionSection(version, true)}
                    {idx < previousVersions.length - 1 && <Divider borderColor="neutral.100" mt="12px" />}
                  </Box>
                ))}
              </Box>
            </Collapse>
          )}
          {previousVersions.length > 0 && (
            <Button mt="12px" variant="link" onClick={() => setShowPreviousVersions(!showPreviousVersions)}>
              {showPreviousVersions ? 'Hide previous versions' : 'Show previous versions'}
            </Button>
          )}
        </ModalBody>

        <ModalFooter pt="0" pb="16px" px="20px" flexDirection="column" gap="8px">
          <Button variant="heavy" onClick={handleClose} w="60%" h="38px" fontSize="13px" mx="auto">
            Got it, thanks!
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default NewVersionModal
